import type { AddGoalInput, ReviseGoalInput } from "@repo/api/schemas";
import type { CarePlanDto, GoalTarget } from "@repo/data-store";
import { addGoal, reviseGoal } from "@repo/data-store";
import { asConditionId } from "@repo/domain";
import { z } from "zod";
import { useAppForm } from "@/shared/forms/app-form";

// === Schema & Types ===

const goalPriorityEnum = z.enum(["none", "high", "medium", "low"]);
const goalTargetKindEnum = z.enum(["none", "quantity", "text"]);
/** `""` ⇒ no comparator (exact). */
const goalComparatorEnum = z.enum(["", "<", "<=", ">=", ">"]);

export const goalFormSchema = z
  .object({
    description: z.string().min(1, "Goal description is required"),
    priority: goalPriorityEnum,
    startDate: z.date().nullable(),
    /** Condition ids the goal targets. */
    addresses: z.array(z.string()),
    // — Target (flattened; assembled into a GoalTarget on submit) —
    targetKind: goalTargetKindEnum,
    targetMeasure: z.string(),
    targetComparator: goalComparatorEnum,
    targetValue: z.number().nullable(),
    targetUnit: z.string(),
    targetText: z.string(),
    targetDue: z.date().nullable(),
  })
  .superRefine((v, ctx) => {
    if (v.targetKind === "none") return;
    if (!v.targetMeasure.trim()) {
      ctx.addIssue({ code: "custom", path: ["targetMeasure"], message: "Measure is required" });
    }
    if (v.targetKind === "quantity" && v.targetValue == null) {
      ctx.addIssue({ code: "custom", path: ["targetValue"], message: "Target value is required" });
    }
    if (v.targetKind === "text" && !v.targetText.trim()) {
      ctx.addIssue({ code: "custom", path: ["targetText"], message: "Target is required" });
    }
  });

export type GoalFormValues = z.infer<typeof goalFormSchema>;

export const EMPTY_GOAL_FORM: GoalFormValues = {
  description: "",
  priority: "none",
  startDate: null,
  addresses: [],
  targetKind: "none",
  targetMeasure: "",
  targetComparator: "",
  targetValue: null,
  targetUnit: "",
  targetText: "",
  targetDue: null,
};

type GoalDto = CarePlanDto["goals"][number];

// === Date conversion (FHIR `date` is YYYY-MM-DD; form uses a local Date) ===

/** Parse a FHIR date (YYYY-MM-DD) into a local Date, avoiding UTC drift. */
function fromFhirDate(s: string | undefined): Date | null {
  if (!s) return null;
  const [y, m, d] = s.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

/** Format a local Date as a FHIR date (YYYY-MM-DD). */
function toFhirDate(d: Date | null): string | undefined {
  if (!d) return undefined;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// === Target ↔ form ===

/** Assemble a GoalTarget from the form fields; `null` when there is no target. */
function formToTarget(v: GoalFormValues): GoalTarget | null {
  if (v.targetKind === "none") return null;
  const due = toFhirDate(v.targetDue);
  const shared = { measure: v.targetMeasure.trim(), ...(due ? { due } : {}) };
  return v.targetKind === "quantity"
    ? {
        kind: "quantity",
        ...shared,
        value: v.targetValue ?? 0,
        ...(v.targetComparator ? { comparator: v.targetComparator } : {}),
        ...(v.targetUnit.trim() ? { unit: v.targetUnit.trim() } : {}),
      }
    : { kind: "text", ...shared, text: v.targetText.trim() };
}

/** Flatten a goal's target into form fields. */
function targetToForm(
  target: GoalTarget | undefined,
): Pick<
  GoalFormValues,
  "targetKind" | "targetMeasure" | "targetComparator" | "targetValue" | "targetUnit" | "targetText" | "targetDue"
> {
  if (!target) {
    return {
      targetKind: "none",
      targetMeasure: "",
      targetComparator: "",
      targetValue: null,
      targetUnit: "",
      targetText: "",
      targetDue: null,
    };
  }
  const base = { targetMeasure: target.measure, targetDue: fromFhirDate(target.due) };
  if (target.kind === "quantity") {
    return {
      ...base,
      targetKind: "quantity",
      targetComparator: target.comparator ?? "",
      targetValue: target.value,
      targetUnit: target.unit ?? "",
      targetText: "",
    };
  }
  return {
    ...base,
    targetKind: "text",
    targetComparator: "",
    targetValue: null,
    targetUnit: "",
    targetText: target.text,
  };
}

/** Prefill form values from an existing goal (edit dialog). */
export function goalToFormValues(goal: GoalDto): GoalFormValues {
  return {
    description: goal.description,
    priority: goal.priority,
    startDate: fromFhirDate(goal.startDate),
    addresses: goal.addresses ?? [],
    ...targetToForm(goal.target),
  };
}

// === Submit ===

/**
 * The select needs a value for "not ranked", so the FORM carries `none`. The
 * intent does not — it simply omits the field. The sentinel is a UI concern and
 * is spent here, at the boundary that invented it.
 */
function toIntentPriority(priority: GoalFormValues["priority"]): AddGoalInput["priority"] {
  return priority === "none" ? undefined : `${priority}-priority`;
}

/** Add a new goal from validated form values. */
export function addGoalFromForm(value: GoalFormValues, carePlanId: AddGoalInput["carePlanId"]): void {
  addGoal({
    carePlanId,
    description: value.description.trim(),
    priority: toIntentPriority(value.priority),
    startDate: toFhirDate(value.startDate),
    addresses: value.addresses.map(asConditionId),
    target: formToTarget(value) ?? undefined,
  });
}

/**
 * Revise an existing goal from validated form values. `targetKind: "none"` clears the target.
 * `achievementStatus` is not edited here — progress is recorded as a separate event.
 */
export function reviseGoalFromForm(
  value: GoalFormValues,
  carePlanId: ReviseGoalInput["carePlanId"],
  goalId: ReviseGoalInput["goalId"],
): void {
  reviseGoal({
    carePlanId,
    goalId,
    description: value.description.trim(),
    priority: toIntentPriority(value.priority) ?? null,
    startDate: toFhirDate(value.startDate),
    addresses: value.addresses.map(asConditionId),
    target: formToTarget(value),
  });
}

export { useAppForm };
